/**
 * AllocationBoard — the Schichtstart board as a SPATIAL MAP of the ward.
 *
 * The organizing idea is geographic continuity: a nurse's patients should stay
 * physically together. So the board mirrors the physical ward — patients live
 * in ROOM SECTIONS (grouped by `room`, ordered by `bed`), not in a flat list or
 * per-nurse lanes. Each on-shift nurse carries a stable identity colour; an
 * assigned bed shows that colour on a left rail + a name chip, so "whose
 * patients" reads at a glance and, crucially, whether a nurse is SPREAD across
 * rooms. Splitting a nurse across rooms is surfaced (amber) but never blocked —
 * floats and 1:1 acuity are valid reasons; the lead has authority.
 *
 * Two speeds of assignment: per-bed (tap a nurse) and per-room ("Ganzes Zimmer
 * zuweisen" — the common case, a whole room to one nurse). The agent's latest
 * proposal per bed is a ghost the lead confirms with one tap.
 *
 * The agent proposes; the LEAD executes (PUT /patients/{id}/assignment). Custody
 * truth is the ward allocation read — server-authoritative, never optimistic
 * (assignments rewrite ACL grants). Speed comes from instant rendering +
 * targeted invalidation, not from a provisional local guess.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bed, Check, DoorOpen, UserRoundPlus, X } from 'lucide-react';
import {
  setPatientAssignment,
  type WardAllocationNurse,
  type WardAllocationPatient,
  type WardAllocationResponse,
} from '@/api/endpoints/shifts';
import { ApiError } from '@/api/client';
import { errorToast } from '@/hooks/errorToast';
import { shiftKeys } from '@/hooks/useShifts';
import { cn } from '@/lib/utils';
import { ProgressBar } from '@/components/ui/progress-bar';
import { userColor } from '@/lib/colors';
import { patientCodename } from '@/lib/patientCodename';
import type { AllocationCommand } from './foldAllocation';

interface AllocationBoardProps {
  ward: WardAllocationResponse;
  /** Latest agent proposal per patient (fold of allocation_commands). */
  proposals: AllocationCommand[];
  /** Only the lead (session author) gets assign affordances. */
  canAssign: boolean;
}

const NO_ROOM = '__no_room__';

// A nurse's colour is `userColor(name, id)` from @/lib/colors — shared
// across surfaces so she reads as the same colour everywhere. The name is
// always shown too; colour (a rail + a dot) only reinforces "whose bed" and
// "who is spread".

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Numeric-aware compare (so "2" sorts before "10"); empty/undefined sorts last. */
function naturalCompare(a?: string, b?: string): number {
  const aa = a?.trim() ?? '';
  const bb = b?.trim() ?? '';
  if (!aa && !bb) return 0;
  if (!aa) return 1;
  if (!bb) return -1;
  return collator.compare(aa, bb);
}

/**
 * Thrown when a (possibly multi-bed) assign has ANY failures, carrying the
 * counts so the toast can tell a partial success from a total one — at shift
 * change "could not assign" must not read as total when most beds landed.
 */
class AssignBatchError extends Error {
  constructor(
    readonly failed: number,
    readonly total: number,
    readonly reason: unknown,
  ) {
    super(`assign batch: ${failed}/${total} failed`);
  }
}

interface RoomGroup {
  key: string;
  /** null → the "Ohne Zimmer" section. */
  room: string | null;
  patients: WardAllocationPatient[];
}

/** Group patients into room sections, beds ordered within; no-room section last. */
function groupByRoom(patients: WardAllocationPatient[]): RoomGroup[] {
  const map = new Map<string, WardAllocationPatient[]>();
  for (const p of patients) {
    const key = p.room?.trim() ? p.room.trim() : NO_ROOM;
    const bucket = map.get(key);
    if (bucket) bucket.push(p);
    else map.set(key, [p]);
  }
  const groups: RoomGroup[] = Array.from(map.entries()).map(([key, ps]) => ({
    key,
    room: key === NO_ROOM ? null : key,
    patients: ps.sort((a, b) => naturalCompare(a.bed, b.bed)),
  }));
  groups.sort((a, b) => {
    if (a.key === NO_ROOM) return 1;
    if (b.key === NO_ROOM) return -1;
    return naturalCompare(a.room ?? undefined, b.room ?? undefined);
  });
  return groups;
}

/** The distinct rooms a nurse holds — the continuity signal is `split` (>1). */
function nurseRooms(
  nurseId: string,
  patients: WardAllocationPatient[],
): { realRooms: string[]; hasNoRoom: boolean; split: boolean } {
  const keys = new Set<string>();
  for (const p of patients) {
    if (p.nurse_id !== nurseId) continue;
    keys.add(p.room?.trim() ? p.room.trim() : NO_ROOM);
  }
  const all = Array.from(keys);
  const realRooms = all
    .filter((k) => k !== NO_ROOM)
    .sort((a, b) => naturalCompare(a, b));
  return { realRooms, hasNoRoom: all.includes(NO_ROOM), split: all.length > 1 };
}

/** The picker target: one bed, or a whole room's unassigned beds. */
type PickerTarget =
  | { type: 'patient'; id: string }
  | { type: 'room'; key: string }
  | null;

export function AllocationBoard({ ward, proposals, canAssign }: AllocationBoardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // One picker open at a time — calm, not modal.
  const [picker, setPicker] = useState<PickerTarget>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: shiftKeys.all });
  };

  const describeError = (error: unknown): string => {
    const notOnShift =
      error instanceof ApiError && error.code === 'INCOMING_NURSE_NOT_ON_SHIFT';
    return notOnShift
      ? t('allocation.notOnShift', { defaultValue: 'Not on shift anymore.' })
      : t('allocation.error', { defaultValue: 'Could not assign — try again.' });
  };

  // Assign a set of beds to one nurse — one bed and a whole room's unassigned
  // beds are the same operation. Custody is server-authoritative: fan the PUTs
  // out in parallel, then invalidate so the board reflects whatever landed; if
  // any fail the toast is honest that some did not.
  const assign = useMutation({
    mutationFn: async ({
      patientIds,
      nurseId,
    }: {
      patientIds: string[];
      nurseId: string;
      /** Set for a whole-room assign — drives that room button's pending state. */
      roomKey?: string;
    }) => {
      const results = await Promise.allSettled(
        patientIds.map((id) => setPatientAssignment(id, { nurse_id: nurseId })),
      );
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      if (failed.length > 0) {
        throw new AssignBatchError(failed.length, results.length, failed[0].reason);
      }
      return results;
    },
    onSuccess: () => setPicker(null),
    onError: (error) => {
      const description =
        error instanceof AssignBatchError && error.failed < error.total
          ? t('allocation.board.partialAssign', {
              defaultValue: '{{failed}} of {{total}} beds could not be assigned.',
              failed: error.failed,
              total: error.total,
            })
          : describeError(error instanceof AssignBatchError ? error.reason : error);
      errorToast({ context: 'allocationBoard', description });
    },
    onSettled: invalidate,
  });

  const proposalFor = (patientId: string) =>
    proposals.find((p) => p.patient_id === patientId);

  const rooms = useMemo(() => groupByRoom(ward.patients), [ward.patients]);

  const total = ward.patients.length;
  const done = ward.patients.filter((p) => !!p.nurse_id).length;

  const bedPending = (patientId: string) =>
    assign.isPending && !!assign.variables?.patientIds.includes(patientId);
  const roomPending = (key: string) =>
    assign.isPending && assign.variables?.roomKey === key;
  const anyPending = assign.isPending;

  return (
    <div className="space-y-4 p-4">
      {/* Completeness — the ritual's progress, ward-level ("you can go"). */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-[15px] font-semibold tracking-tight text-neutral-900">
            {t('allocation.board.title', { defaultValue: 'Schichtstart' })}
          </p>
          <p
            className={cn(
              'text-[14px] font-semibold tabular-nums',
              done === total && total > 0 ? 'text-primary' : 'text-neutral-600',
            )}
          >
            {t('allocation.board.progress', {
              defaultValue: '{{done}} of {{total}} assigned',
              done,
              total,
            })}
          </p>
        </div>
        <ProgressBar
          value={done}
          max={total}
          label={t('allocation.board.progressLabel', {
            defaultValue: 'Patient assignment progress',
          })}
          className="mt-2 h-1.5 bg-neutral-100"
        />
        {done === total && total > 0 && (
          <p className="mt-2 text-[13px] font-semibold text-primary">
            {t('allocation.board.complete', {
              defaultValue: 'All patients assigned — the ward is handed over.',
            })}
          </p>
        )}
      </div>

      {/* Nurse strip — the colour legend + per-nurse load + continuity signal. */}
      {ward.on_shift.length === 0 ? (
        <p className="rounded-lg border border-neutral-200 bg-white p-4 text-[14px] text-neutral-500">
          {t('allocation.board.nobodyOnShift', {
            defaultValue:
              'Nobody is on shift yet — patients can be assigned once a nurse starts her shift.',
          })}
        </p>
      ) : (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {ward.on_shift.map((n) => (
            <NurseChip key={n.nurse_id} nurse={n} patients={ward.patients} />
          ))}
        </div>
      )}

      {/* The ward, room by room. Beds are the tiles. */}
      {total === 0 ? (
        <p className="rounded-lg border border-neutral-200 bg-white p-6 text-center text-[14px] text-neutral-500">
          {t('allocation.board.noPatients', {
            defaultValue: 'No patients on the ward.',
          })}
        </p>
      ) : (
        <div className="space-y-4">
          {rooms.map((group) => {
            const unassigned = group.patients.filter((p) => !p.nurse_id);
            const assignedCount = group.patients.length - unassigned.length;
            const roomPickerOpen = picker?.type === 'room' && picker.key === group.key;
            return (
              <section key={group.key} className="space-y-2">
                <div className="flex min-h-[32px] items-center justify-between gap-2">
                  <h3 className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
                    <DoorOpen className="h-3.5 w-3.5" aria-hidden="true" />
                    {group.room
                      ? t('allocation.board.room', {
                          defaultValue: 'Zimmer {{room}}',
                          room: group.room,
                        })
                      : t('allocation.board.noRoom', { defaultValue: 'Ohne Zimmer' })}
                    <span className="font-normal normal-case tabular-nums text-neutral-400">
                      {assignedCount}/{group.patients.length}
                    </span>
                  </h3>
                  {canAssign && unassigned.length > 0 && !roomPickerOpen && (
                    <button
                      type="button"
                      onClick={() => setPicker({ type: 'room', key: group.key })}
                      disabled={anyPending}
                      className={cn(
                        'inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3.5',
                        'border border-primary-200 text-[13px] font-semibold text-primary',
                        'transition active:scale-95 disabled:opacity-50',
                      )}
                    >
                      <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
                      {roomPending(group.key)
                        ? t('allocation.assigning', { defaultValue: 'Assigning…' })
                        : t('allocation.board.assignRoom', {
                            defaultValue: 'Ganzes Zimmer zuweisen',
                          })}
                    </button>
                  )}
                </div>

                {canAssign && roomPickerOpen && (
                  <div className="rounded-lg border border-primary-100 bg-primary-50 p-3">
                    <p className="mb-2 text-[13px] font-semibold text-neutral-700">
                      {t('allocation.board.assignRoomTo', {
                        defaultValue: 'Ganzes Zimmer zuweisen an…',
                      })}
                    </p>
                    <NursePicker
                      nurses={ward.on_shift}
                      pending={assign.isPending}
                      onPick={(nurseId) =>
                        assign.mutate({
                          roomKey: group.key,
                          patientIds: unassigned.map((p) => p.patient_id),
                          nurseId,
                        })
                      }
                      onClose={() => setPicker(null)}
                    />
                  </div>
                )}

                <ul className="space-y-2">
                  {group.patients.map((p) => (
                    <BedTile
                      key={p.patient_id}
                      patient={p}
                      proposal={proposalFor(p.patient_id)}
                      canAssign={canAssign}
                      pending={bedPending(p.patient_id)}
                      pickerOpen={picker?.type === 'patient' && picker.id === p.patient_id}
                      onOpenPicker={() => setPicker({ type: 'patient', id: p.patient_id })}
                      onAssign={(nurseId) =>
                        assign.mutate({ patientIds: [p.patient_id], nurseId })
                      }
                      onClose={() => setPicker(null)}
                      nurses={ward.on_shift}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NurseChip({
  nurse,
  patients,
}: {
  nurse: WardAllocationNurse;
  patients: WardAllocationPatient[];
}) {
  const { t } = useTranslation();
  const color = userColor(nurse.name, nurse.nurse_id);
  const { realRooms, hasNoRoom, split } = nurseRooms(nurse.nurse_id, patients);
  // Only rendered under `split` (>1 distinct room), which implies realRooms >= 1.
  const roomsLabel = [
    t('allocation.board.roomsList', {
      defaultValue: 'Zimmer {{rooms}}',
      rooms: realRooms.join(', '),
    }),
    hasNoRoom ? t('allocation.board.noRoom', { defaultValue: 'Ohne Zimmer' }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className="flex shrink-0 flex-col gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-2"
      style={{ minWidth: '9rem' }}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <span className="truncate text-[14px] font-semibold text-neutral-900">
          {nurse.name}
        </span>
      </div>
      <span className="text-[12px] tabular-nums text-neutral-500">
        {t('allocation.board.load', {
          defaultValue_one: '{{count}} patient',
          defaultValue_other: '{{count}} patients',
          count: nurse.patient_count,
        })}
      </span>
      {split && (
        <span
          className="inline-flex items-center gap-1 text-[12px] font-medium text-citation-700"
          title={t('allocation.board.spread', {
            defaultValue: 'Über mehrere Zimmer verteilt',
          })}
        >
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-citation-500"
            aria-hidden="true"
          />
          {roomsLabel} · {t('allocation.board.split', { defaultValue: 'geteilt' })}
        </span>
      )}
    </div>
  );
}

function BedTile({
  patient,
  proposal,
  canAssign,
  pending,
  pickerOpen,
  onOpenPicker,
  onAssign,
  onClose,
  nurses,
}: {
  patient: WardAllocationPatient;
  proposal?: AllocationCommand;
  canAssign: boolean;
  pending: boolean;
  pickerOpen: boolean;
  onOpenPicker: () => void;
  onAssign: (nurseId: string) => void;
  onClose: () => void;
  nurses: WardAllocationNurse[];
}) {
  const { t } = useTranslation();
  const assigned = !!patient.nurse_id;
  const color = patient.nurse_id ? userColor(patient.nurse_name, patient.nurse_id) : null;
  const proposalNurseId = proposal?.nurse_id;

  return (
    <li
      className={cn(
        'overflow-hidden rounded-lg border transition-colors duration-200 motion-reduce:transition-none',
        assigned ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50',
      )}
      style={{ borderLeftWidth: '4px', borderLeftColor: color ?? 'var(--color-neutral-300)' }}
    >
      <div className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-2 p-3">
        {patient.bed?.trim() && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-neutral-100 px-1.5 py-0.5 text-[12px] font-semibold tabular-nums text-neutral-600">
            <Bed className="h-3.5 w-3.5" aria-hidden="true" />
            {patient.bed.trim()}
          </span>
        )}
        {/* Stable token-derived codename as the identity — not the backend
            `patient.label` (the `{ward}-{bed}` placement), which is redundant
            on a board already grouped by room/bed. `label` stays on the
            WardAllocationPatient contract for its other consumer, the roster
            Shift Planner board. */}
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-neutral-900">
          {patientCodename(patient.patient_id)}
        </span>

        {assigned && color && (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-[13px] font-semibold text-neutral-800">
            <Check className="h-3.5 w-3.5 shrink-0" style={{ color }} aria-hidden="true" />
            <span className="max-w-[9rem] truncate">{patient.nurse_name}</span>
          </span>
        )}

        {canAssign && !pickerOpen && (
          <div className="flex shrink-0 items-center gap-1.5">
            {!assigned && proposal && proposalNurseId && (
              <button
                type="button"
                onClick={() => onAssign(proposalNurseId)}
                disabled={pending}
                className={cn(
                  'inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4',
                  'bg-primary text-[14px] font-semibold text-primary-foreground',
                  'transition active:scale-95 disabled:opacity-50',
                )}
              >
                <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
                {pending
                  ? t('allocation.assigning', { defaultValue: 'Assigning…' })
                  : proposal.nurse}
              </button>
            )}
            <button
              type="button"
              onClick={onOpenPicker}
              disabled={pending}
              className="min-h-[44px] rounded-full px-3 text-[14px] font-semibold text-primary transition active:scale-95 disabled:opacity-50"
            >
              {assigned || proposalNurseId
                ? t('allocation.board.change', { defaultValue: 'Change' })
                : t('allocation.board.assign', { defaultValue: 'Assign' })}
            </button>
          </div>
        )}
      </div>

      {!assigned && proposalNurseId && !pickerOpen && (
        <p className="px-3 pb-2 text-[12px] text-neutral-500">
          {t('allocation.board.proposalHint', {
            defaultValue: 'Suggested: {{nurse}}',
            nurse: proposal?.nurse,
          })}
        </p>
      )}

      {canAssign && pickerOpen && (
        <div className="px-3 pb-3">
          <NursePicker
            nurses={
              patient.nurse_id
                ? nurses.filter((o) => o.nurse_id !== patient.nurse_id)
                : nurses
            }
            pending={pending}
            onPick={onAssign}
            onClose={onClose}
          />
        </div>
      )}
    </li>
  );
}

function NursePicker({
  nurses,
  pending,
  onPick,
  onClose,
}: {
  nurses: WardAllocationNurse[];
  pending: boolean;
  onPick: (nurseId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      {nurses.map((n) => {
        const color = userColor(n.name, n.nurse_id);
        return (
          <button
            key={n.nurse_id}
            type="button"
            onClick={() => onPick(n.nurse_id)}
            disabled={pending}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5',
              'text-[14px] font-medium text-neutral-900 transition active:scale-95 disabled:opacity-50',
            )}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: color }}
              aria-hidden="true"
            />
            {n.name}
          </button>
        );
      })}
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.cancel')}
        className="grid h-11 w-11 place-items-center rounded-full text-neutral-500 transition active:scale-95"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
